import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import OverviewTab from "./components/OverviewTab";
import TimelineTab from "./components/TimelineTab";
import MissionBoardTab from "./components/MissionBoardTab";
import DocumentsTab from "./components/DocumentsTab";
import ReadOnlyTablePlan from "./components/ReadOnlyTablePlan";
import GuestsTab from "./components/GuestsTab";
import DietaryTab from "./components/DietaryTab";
import { TabSkeleton } from "./components/ui";
import Vendors from "../Vendors";
import { LayoutDashboard, Calendar, Users, Shield, LogOut, Briefcase, FolderOpen, UtensilsCrossed, Menu, X, Table2, UserRound } from "lucide-react";
import { toast } from "sonner";

import collaboratorApi, { clearCollaboratorToken } from "../../lib/collaboratorApi";
import { sanitizeCollaboratorStorage } from "./collaboratorAccess";
import { normalizeCollaboratorPermissions } from "./collaboratorPermissions";

const TAB_IDS = ["overview", "timeline", "mission", "documents", "tables", "vendors", "guests", "dietary"];
const readHashTab = () => {
  if (typeof window === "undefined") return "overview";
  const id = window.location.hash.replace("#", "");
  return TAB_IDS.includes(id) ? id : "overview";
};

// One list drives the sidebar, the phone menu and the bottom bar.
function buildNavItems(permissions) {
  return [
    { id: "overview", label: "Aperçu", icon: LayoutDashboard, allowed: true, primary: true },
    { id: "timeline", label: "Planning", icon: Calendar, allowed: permissions.timeline, primary: true },
    { id: "mission", label: "Missions", icon: Briefcase, allowed: permissions.mission, primary: true },
    { id: "documents", label: "Documents", icon: FolderOpen, allowed: permissions.documents },
    { id: "tables", label: "Plan de table", shortLabel: "Plan", icon: Table2, allowed: permissions.tables, primary: true },
    { id: "guests", label: "Invités", icon: Users, allowed: permissions.guests, primary: true },
    { id: "dietary", label: "Restrictions", icon: UtensilsCrossed, allowed: permissions.dietary_restrictions },
    { id: "vendors", label: "Prestataires", icon: Shield, allowed: permissions.vendors },
  ].filter((item) => item.allowed);
}

const RESOURCE_ENDPOINTS = {
  guests: "/collaborator-portal/guests",
  dietary: "/collaborator-portal/dietary-restrictions",
  tables: "/collaborator-portal/tables",
  vendors: "/collaborator-portal/vendors",
};

export default function CollaboratorPortal() {
  const [activeTab, setActiveTabState] = useState(readHashTab);
  const [session, setSession] = useState(null);
  const [resourceData, setResourceData] = useState({});
  const [resourceError, setResourceError] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const navigate = useNavigate();

  // The current tab lives in the address (#guests): reload and the browser's
  // back button keep the place instead of dropping back to the overview.
  const setActiveTab = useCallback((id) => {
    setActiveTabState(id);
    setResourceError("");
    if (typeof window !== "undefined") window.history.replaceState(null, "", id === "overview" ? window.location.pathname : `#${id}`);
  }, []);

  useEffect(() => {
    const onHashChange = () => setActiveTabState(readHashTab());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    sanitizeCollaboratorStorage();
    let ignore = false;

    const fetchSession = () => {
      collaboratorApi
        .get("/collaborator-auth/me")
        .then((response) => {
          if (!ignore) setSession(response.data);
        })
        .catch((error) => {
          if (ignore) return;
          if (error.response?.status >= 500) {
            setResourceError("Le backend collaborateur est indisponible pour le moment.");
            return;
          }
          // Only redirect on actual 401/403 errors, avoid redirecting if it's a network error
          if (error.response?.status === 401 || error.response?.status === 403) {
            clearCollaboratorToken();
            navigate("/collaborator-login");
          }
        });
    };

    fetchSession();
    const intervalId = setInterval(fetchSession, 10000);

    return () => {
      ignore = true;
      clearInterval(intervalId);
    };
  }, [navigate]);

  const permissions = useMemo(
    () => normalizeCollaboratorPermissions(session?.permissions),
    [session],
  );
  const navItems = useMemo(() => buildNavItems(permissions), [permissions]);

  useEffect(() => {
    if (!session) return;
    const endpoint = RESOURCE_ENDPOINTS[activeTab];
    if (!endpoint || resourceData[activeTab]) return;

    collaboratorApi
      .get(endpoint)
      .then((response) => {
        setResourceData((prev) => ({ ...prev, [activeTab]: response.data }));
      })
      .catch((error) => {
        setResourceError(error.response?.data?.detail || "Impossible de charger cette section.");
      });
  }, [activeTab, resourceData, session]);

  async function handleLogout() {
    try {
      await collaboratorApi.post("/collaborator-auth/logout");
    } catch (error) {
      console.error(error);
    } finally {
      clearCollaboratorToken();
      toast.success("Déconnexion collaborateur effectuée.");
      navigate("/collaborator-login");
    }
  }

  if (resourceError && !session) {
    return <div className="min-h-screen bg-offwhite flex items-center justify-center p-6 text-sm text-red-700">{resourceError}</div>;
  }

  if (!session) {
    return (
      <div className="mx-auto max-w-[1100px] px-4 pt-28 md:px-16">
        <TabSkeleton />
      </div>
    );
  }

  const weddingLabel = session?.owner_user_id ? "Mariage sécurisé Ceremind" : "Espace collaborateur";
  const bottomItems = navItems.filter((item) => item.primary).slice(0, 4);
  const goTo = (id) => { setActiveTab(id); setMobileNavOpen(false); window.scrollTo?.({ top: 0 }); };

  return (
    <div className="relative min-h-screen w-full flex flex-col text-[#211d19]">
      <div className="flex-1 flex flex-col pointer-events-none">
        <nav className="collab-portal-topbar fixed top-0 z-50 w-full pointer-events-auto">
          <div className="mx-auto flex w-full max-w-[1440px] items-center justify-between px-4 py-3.5 md:px-10 lg:py-4">
            <div className="min-w-0 font-serif text-lg tracking-wide text-[#211d19] md:text-2xl">{weddingLabel}</div>
            <div className="flex items-center gap-3">
              <div className="hidden items-center gap-2 rounded-full border border-[#cfdccb] bg-[#eef3ec] px-3 py-1.5 text-[#3f5a48] md:flex">
                <Shield className="h-4 w-4" aria-hidden="true" />
                <span className="text-[11px] font-bold uppercase tracking-wider">{session.role}</span>
              </div>
              <button onClick={handleLogout} className="hidden items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-[#5b5247] transition-colors hover:bg-white hover:text-red-600 md:flex">
                <LogOut className="h-4 w-4" aria-hidden="true" />
                Déconnexion
              </button>
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#e2d9c8] bg-white text-[#5b5247] shadow-sm" title={session.name}>
                <UserRound className="h-5 w-5" aria-hidden="true" />
              </span>
            </div>
          </div>
        </nav>

        {mobileNavOpen ? (
          <div className="pointer-events-auto fixed inset-0 z-[60] touch-none lg:hidden">
            <button
              type="button"
              aria-label="Fermer le menu"
              onClick={() => setMobileNavOpen(false)}
              className="absolute inset-0 h-full w-full bg-[#211d19]/35 backdrop-blur-[2px]"
            />
            <nav aria-label="Navigation collaborateur" className="absolute inset-x-3 bottom-24 max-h-[calc(100dvh-8rem)] touch-pan-y overflow-y-auto overscroll-contain rounded-3xl border border-[#e9e1d4] bg-white p-3 shadow-2xl">
              <div className="flex items-center justify-between px-3 py-2">
                <div>
                  <p className="text-sm font-semibold text-[#211d19]">{session.name}</p>
                  <p className="text-xs text-[#6a6055]">{session.role}</p>
                </div>
                <button type="button" onClick={() => setMobileNavOpen(false)} className="rounded-full p-2 text-[#211d19]" aria-label="Fermer le menu"><X className="h-5 w-5" /></button>
              </div>
              <div className="mt-1 flex flex-col gap-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => goTo(item.id)}
                      className={`flex items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm font-semibold ${activeTab === item.id ? "bg-[#eef3ec] text-[#211d19]" : "text-[#5b5247] hover:bg-[#f6f1e8]"}`}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                      {item.label}
                    </button>
                  );
                })}
              </div>
              <button type="button" onClick={handleLogout} className="mt-3 flex w-full items-center gap-3 rounded-2xl border border-red-100 px-4 py-3 text-left text-sm font-semibold text-red-600"><LogOut className="h-5 w-5" aria-hidden="true" />Déconnexion</button>
            </nav>
          </div>
        ) : null}

        <aside className="collab-portal-sidebar pointer-events-auto fixed left-0 top-0 z-40 hidden h-full w-64 flex-col p-4 pt-28 lg:flex">
          <div className="mb-4 flex flex-col items-center border-b border-[#e9e1d4] px-4 pb-6">
            <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full border border-[#cfdccb] bg-[#eef3ec] font-serif text-2xl text-[#3f5a48]">
              <span>{(session.name || "C").charAt(0)}</span>
            </div>
            <h2 className="text-center font-serif text-xl text-[#211d19]">{session.name}</h2>
            <p className="mt-1 break-all text-center text-[11px] text-[#6a6055]">{session.email}</p>
          </div>
          <nav aria-label="Rubriques" className="flex flex-1 flex-col gap-1.5">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => goTo(item.id)}
                  aria-current={active ? "page" : undefined}
                  className={`flex items-center gap-3 rounded-2xl px-4 py-3 text-left transition-colors ${active ? "bg-[#211d19] text-white shadow-md" : "text-[#4a4239] hover:bg-white"}`}
                >
                  <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                  <span className="text-sm font-semibold">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="pointer-events-auto mx-auto flex w-full max-w-[1280px] flex-1 items-start justify-center px-4 pb-32 pt-24 md:px-10 lg:ml-64 lg:pb-16 lg:pt-28">
          <div className="w-full max-w-[1100px]">
            {resourceError ? (
              <div role="alert" className="mb-4 w-full rounded-2xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">{resourceError}</div>
            ) : null}
            {activeTab === "overview" ? <OverviewTab permissions={permissions} onNavigate={goTo} /> : null}
            {activeTab === "timeline" ? <TimelineTab /> : null}
            {activeTab === "mission" ? <MissionBoardTab /> : null}
            {activeTab === "documents" ? <DocumentsTab /> : null}
            {activeTab === "guests" ? <GuestsTab guests={resourceData.guests} /> : null}
            {activeTab === "dietary" ? <DietaryTab guests={resourceData.dietary} /> : null}
            {activeTab === "tables" ? <ReadOnlyTablePlan plan={resourceData.tables} /> : null}
            {activeTab === "vendors" ? (
              <div className="w-full rounded-3xl border border-[#e9e1d4] bg-white p-4 shadow-sm md:p-6">
                <Vendors />
              </div>
            ) : null}
          </div>
        </main>

        <nav aria-label="Navigation rapide" className="pointer-events-auto fixed inset-x-0 bottom-0 z-50 border-t border-[#e9e1d4] bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden">
          <div className="mx-auto flex max-w-xl items-stretch justify-around px-2 py-1.5">
            {bottomItems.map((item) => {
              const Icon = item.icon;
              const active = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => goTo(item.id)}
                  aria-current={active ? "page" : undefined}
                  className={`flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-xl px-1 py-1.5 text-[11px] font-semibold transition-colors ${active ? "text-[#211d19]" : "text-[#7a6f62]"}`}
                >
                  <span className={`flex h-7 w-12 items-center justify-center rounded-full transition-colors ${active ? "bg-[#e3ece0]" : ""}`}><Icon className="h-5 w-5" aria-hidden="true" /></span>
                  <span className="max-w-full truncate">{item.shortLabel || item.label}</span>
                </button>
              );
            })}
            <button
              type="button"
              aria-label={mobileNavOpen ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen((open) => !open)}
              className="flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-xl px-1 py-1.5 text-[11px] font-semibold text-[#7a6f62]"
            >
              <span className="flex h-7 w-12 items-center justify-center">{mobileNavOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}</span>
              <span>Plus</span>
            </button>
          </div>
        </nav>
      </div>
    </div>
  );
}
